import { useState } from 'react';
import { httpClient } from '../api/http';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Alert } from '@piggytechng/component-library';
import { authPayload } from '../helpers/utils/auth-payload';
import useRolePermissions from './useRolePermissions';
import { FlexDollarQuickAction } from '../types';

const useFlexDollar = () => {
  const { isSuperAdmin } = useRolePermissions({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [flexDollarTransaction, setFlexDollarTransaction] = useState();
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [isRunningQuickAction, setIsRunningQuickAction] = useState<boolean>(false);
  const [searchedTransactions, setSearchedTransactions] = useState<any[] | null>(null);

  const params = useParams();
  const userIdIn = params.id as string;

  const [transactionsParams] = useState({
    page: 1,
    limit: 20,
  });

  const {
    data: flexDollarBalance,
    isLoading: isLoadingFlexDollarBalance,
    refetch: refetchFlexDollarBalance,
  } = useQuery({
    queryKey: ['flex-dollar-balance' + userIdIn],
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    queryFn: () => httpClient.get(`admin/flexdollar/balance/${userIdIn}`).then((res) => res.data.data?.balance),
  });

  const {
    data: flexDollarTransactionQ,
    isLoading: isLoadingFlexDollarTransactions,
    refetch: refetchFlexDollarTransactions,
  } = useQuery({
    queryKey: ['flex-dollar-transactions' + userIdIn + transactionsParams.page + transactionsParams.limit],
    enabled: !!userIdIn,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    queryFn: () =>
      httpClient
        .get(`admin/flexdollar/transactions/${userIdIn}/${transactionsParams.page}/${transactionsParams.limit}`)
        .then((res) => res.data.data?.transactions),
  });

  const {
    data: flexDollarRate,
    // isLoading: isLoadingFlexNairaTransactions,
    // refetch: refetchFlexNairaTransactions,
  } = useQuery({
    queryKey: ['flex-Dollar-Rate'],
    enabled: !!userIdIn,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    queryFn: () => httpClient.get(`admin/flexdollar/rate/${userIdIn}`).then((res) => res.data.data),
    staleTime: 60 * 60 * 1000,
  });

  const {
    data: flexDollarAccountNumber,
    isLoading: isLoadingFlexDollarAccountNumber,
    refetch: refetchFlexDollarAccountNumber,
  } = useQuery({
    queryKey: ['flex-dollar-account-number' + userIdIn],
    enabled: !!userIdIn,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    queryFn: () =>
      httpClient.get(`admin/flexdollar/account-numbers/${userIdIn}`, { hideError: true }).then((res) => res.data?.data),
  });

  const searchFlexDollarTransactions = async (query: string, filter = 'reference', page = 1, limit = 20) => {
    if (!query.trim()) {
      Alert.error('Please enter a search value');
      return;
    }
    try {
      setIsSearching(true);
      const url = `admin/flexdollar/transactions/search/${userIdIn}/${filter}/${page}/${limit}`;
      const response: any = await httpClient.post(url, { query: query.trim() });
      if (response.data.status) {
        const data = response.data.data;
        const results = data?.transactions ?? data?.transaction ?? data?.results ?? (Array.isArray(data) ? data : []);
        setSearchedTransactions(results ?? []);
      }
    } catch (error: any) {
      Alert.error(error?.response?.data?.message || 'Failed to search transactions');
    } finally {
      setIsSearching(false);
    }
  };

  // POST admin/flexdollar/quick-actions { userId, action, period: 'MM-YYYY', identifier, password }.
  // On success the response carries data.worker with the worker's own message; it is shown to the admin.
  const runFlexDollarQuickAction = async (
    action: FlexDollarQuickAction,
    options: { period: string },
    password: string,
    callback?: () => void,
  ) => {
    if (!isSuperAdmin) {
      Alert.warn('You do not have access to perform this action.');
      return;
    }

    try {
      setIsRunningQuickAction(true);
      const response: any = await httpClient.post('admin/flexdollar/quick-actions', {
        userId: userIdIn,
        action,
        period: options.period,
        ...authPayload(password),
      });

      if (response?.data?.status) {
        const worker = response?.data?.data?.worker;
        const workerMessage = typeof worker === 'string' ? worker : worker?.message;
        Alert.success(workerMessage || response?.data?.message || 'Action executed successfully');
        callback?.();
        refetchFlexDollarBalance();
      } else {
        Alert.error(response?.data?.message || 'The action was not applied');
      }
    } catch (error: any) {
      // the http interceptor already toasts the backend message
      console.log(error?.response?.data?.message || 'An error occurred trying to perform the action');
    } finally {
      setIsRunningQuickAction(false);
    }
  };

  const clearFlexDollarSearch = () => {
    setSearchedTransactions(null);
  };

  const fetchFlexDollarTransaction = async (reference: string) => {
    try {
      setIsLoading(true);
      const url = `admin/flexdollar/transaction/${reference}`;
      const response: any = await httpClient.get(url);
      if (response.data.status) {
        setFlexDollarTransaction(response.data.transaction);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isLoadingFlexDollarBalance,
    fetchFlexDollarTransaction,
    flexDollarBalance,
    flexDollarTransaction,
    isLoading,
    flexDollarTransactionQ,
    isLoadingFlexDollarTransactions,
    refetchFlexDollarTransactions,
    refetchFlexDollarBalance,
    flexDollarRate,
    flexDollarAccountNumber,
    isLoadingFlexDollarAccountNumber,
    refetchFlexDollarAccountNumber,
    searchFlexDollarTransactions,
    clearFlexDollarSearch,
    searchedTransactions,
    isSearching,
    runFlexDollarQuickAction,
    isRunningQuickAction,
  };
};

export default useFlexDollar;
